import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import type { DayWorkerRow } from './types';

const emailSchema = z.email();

/**
 * A row nobody has typed into yet — the state a row is in the moment
 * "+ Add Day Worker" creates it. It is not an error, it is just not a worker,
 * so it is left out of the payload and never marked up in red.
 */
export const isEmptyDayWorkerRow = (worker: DayWorkerRow): boolean =>
  !worker.name.trim() && !worker.contact && !worker.dailyRate && !worker.email.trim();

/**
 * A day worker row is filled in a field at a time, and a row that only has a
 * name yet is not wrong — it is unfinished. So an empty contact or email is
 * always accepted, and only one that is filled in but could never be reached is
 * called out.
 *
 * The rules are the ones the profile forms use, so a number the crew list would
 * take is a number this table takes.
 */
export const dayWorkerContactError = (contact: string): string | undefined =>
  !contact || isValidPhoneNumber(contact) ? undefined : 'Please enter a valid mobile number.';

export const dayWorkerEmailError = (email: string): string | undefined => {
  const trimmed = email.trim();
  return !trimmed || emailSchema.safeParse(trimmed).success
    ? undefined
    : 'Please enter a valid email address.';
};

/**
 * The name is the one thing a worker cannot be logged without — a rate or a
 * number with nobody attached says nothing. It is asked for as soon as the row
 * holds anything at all, so a row still waiting to be typed into stays quiet.
 */
export const dayWorkerNameError = (worker: DayWorkerRow): string | undefined =>
  worker.name.trim() || isEmptyDayWorkerRow(worker) ? undefined : 'Please enter a name.';

/**
 * The daily rate is money, so it takes digits and at most two decimals and
 * nothing else. Anything else — letters, a second dot, a third decimal, a
 * pasted "$1,200.567" — is trimmed away as it is typed rather than flagged
 * afterwards.
 */
export const toDailyRateInput = (value: string): string => {
  const [whole, ...decimals] = value.replace(/[^\d.]/g, '').split('.');
  return decimals.length ? `${whole}.${decimals.join('').slice(0, 2)}` : whole;
};

/** Whether any row is holding something the log cannot be saved with. */
export const hasDayWorkerErrors = (workers: DayWorkerRow[]): boolean =>
  workers.some(
    (worker) =>
      dayWorkerNameError(worker) ??
      dayWorkerContactError(worker.contact) ??
      dayWorkerEmailError(worker.email),
  );
